
import { useState, useMemo } from 'react';
import { CalendarEvent } from '../../../mocks/calendarEvents';

interface MonthCalendarProps {
  events: CalendarEvent[];
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  onDateSelect: (date: string) => void;
  selectedDate: string | null;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const categoryColors: Record<string, string> = {
  Concert: 'bg-amber-400',
  Festival: 'bg-rose-400',
  Market: 'bg-emerald-400',
  Family: 'bg-sky-400',
  Sports: 'bg-orange-400',
  'Arts & Culture': 'bg-violet-400',
  Community: 'bg-cyan-400',
  Holiday: 'bg-red-400',
};

export default function MonthCalendar({
  events,
  currentMonth,
  onMonthChange,
  onDateSelect,
  selectedDate,
}: MonthCalendarProps) {
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events.forEach((ev) => {
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    });
    return map;
  }, [events]);

  const prevMonth = () => {
    onMonthChange(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    onMonthChange(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    onMonthChange(new Date(today.getFullYear(), today.getMonth(), 1));
    onDateSelect(todayStr);
  };

  const monthName = currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i;
    const prevM = month === 0 ? 12 : month;
    const prevY = month === 0 ? year - 1 : year;
    cells.push({
      day: d,
      dateStr: `${prevY}-${String(prevM).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      isCurrentMonth: false,
    });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      day: d,
      dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      isCurrentMonth: true,
    });
  }

  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      const nextM = month + 2 > 12 ? 1 : month + 2;
      const nextY = month + 2 > 12 ? year + 1 : year;
      cells.push({
        day: d,
        dateStr: `${nextY}-${String(nextM).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        isCurrentMonth: false,
      });
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-bold text-gray-900">{monthName}</h3>
          <button
            onClick={goToToday}
            className="text-xs font-medium text-teal-600 bg-teal-50 px-3 py-1 rounded-full hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Today
          </button>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={prevMonth}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
          </button>
          <button
            onClick={nextMonth}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {DAYS.map((d) => (
          <div key={d} className="py-2.5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          const dayEvents = eventsByDate[cell.dateStr] || [];
          const isToday = cell.dateStr === todayStr;
          const isSelected = cell.dateStr === selectedDate;
          const isHovered = cell.dateStr === hoveredDate;

          return (
            <button
              key={idx}
              onClick={() => cell.isCurrentMonth && onDateSelect(cell.dateStr)}
              onMouseEnter={() => setHoveredDate(cell.dateStr)}
              onMouseLeave={() => setHoveredDate(null)}
              className={`relative min-h-[90px] p-1.5 border-b border-r border-gray-50 text-left transition-all cursor-pointer ${
                !cell.isCurrentMonth ? 'opacity-30' : ''
              } ${isSelected ? 'bg-teal-50 ring-2 ring-inset ring-teal-400' : ''} ${
                isHovered && !isSelected && cell.isCurrentMonth ? 'bg-gray-50' : ''
              }`}
            >
              <span
                className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                  isToday
                    ? 'bg-teal-600 text-white'
                    : cell.isCurrentMonth
                    ? 'text-gray-700'
                    : 'text-gray-300'
                }`}
              >
                {cell.day}
              </span>
              <div className="mt-0.5 space-y-0.5">
                {dayEvents.slice(0, 3).map((ev) => (
                  <div
                    key={ev.id}
                    className={`${categoryColors[ev.category] || 'bg-gray-400'} rounded px-1 py-0.5 text-[10px] font-medium text-white truncate leading-tight`}
                  >
                    {ev.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-gray-400 font-medium pl-1">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="px-6 py-3 border-t border-gray-100 flex flex-wrap gap-3">
        {Object.entries(categoryColors).map(([cat, color]) => (
          <div key={cat} className="flex items-center space-x-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${color}`}></span>
            <span className="text-[11px] text-gray-500">{cat}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
